import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Message, Table, Label } from 'semantic-ui-react';
import { request, yuan, units, time, states } from './helpers';
import { isAdmin } from '../../helpers';
import './platform.css';
import FinancialOverview from './FinancialOverview';
import WalletLedger from './WalletLedger';
import PricingOperations from './PricingOperations';
import { useSearchParams } from 'react-router-dom';

const blankPrice = {
  model: '',
  reason: '',
  current_id: 0,
  provider: '智谱',
  kind: 'chat',
  vision: false,
  input: '1',
  cacheWrite: '1.25',
  cacheRead: '0.1',
  output: '2',
  costInput: '0',
  costCacheWrite: '0',
  costCacheRead: '0',
  costOutput: '0',
  max_input_tokens: '32768',
  default_output_tokens: '4096',
  max_output_tokens: '4096',
  enabled: true,
  test_price: true,
};
export default function Admin() {
  const [search, setSearch] = useSearchParams();
  const sections = [
    ['overview', '总览'],
    ['entries', '流水'],
    ['prices', '模型定价'],
    ['credit', '人工充值'],
    ['codes', '生成兑换码'],
    ['calls', '调用核算'],
  ];
  const requested = search.get('view') || 'overview';
  const tab = sections.some(([id]) => id === requested)
    ? requested
    : 'overview';
  const [refresh, setRefresh] = useState(0);
  const [page, setPage] = useState(0);
  const [callState, setCallState] = useState('pending');
  const latest = useRef(0);
  const activeView = useRef({ page, callState });
  activeView.current = { page, callState };
  const [prices, setPrices] = useState([]);
  const [calls, setCalls] = useState([]);
  const [price, setPrice] = useState(blankPrice);
  const [credit, setCredit] = useState({
    user_id: '',
    amount: '',
    reason: '',
    business_id: crypto.randomUUID(),
  });
  const [gift, setGift] = useState({
    name: '',
    amount: '',
    count: '1',
    expires_at: '',
  });
  const [codes, setCodes] = useState([]);
  const [resolve, setResolve] = useState({
    request: '',
    release: false,
    input: '0',
    cacheWrite: '0',
    cacheRead: '0',
    output: '0',
    reasoning: '0',
    reason: '',
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  activeView.current = { page, callState, tab };
  const load = useCallback(async () => {
    if (!isAdmin()) return;
    const snapshot = activeView.current;
    const version = ++latest.current;
    if (snapshot.tab !== 'prices' && snapshot.tab !== 'calls') return;
    try {
      const data = await request(
        'get',
        snapshot.tab === 'prices'
          ? '/api/platform/admin/prices'
          : `/api/platform/admin/requests?page=${snapshot.page}&state=${snapshot.callState}`
      );
      if (
        version !== latest.current ||
        snapshot.page !== activeView.current.page ||
        snapshot.callState !== activeView.current.callState ||
        snapshot.tab !== activeView.current.tab
      )
        return;
      if (snapshot.tab === 'prices') setPrices(data);
      else setCalls(data);
    } catch (e) {
      if (version === latest.current) setError(e.message);
    }
  }, []);
  useEffect(() => {
    load();
    return () => {
      latest.current++;
    };
  }, [load, page, callState, tab]);
  async function action(work) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
      await load();
      setNotice('操作成功');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  function priceField(name, value) {
    setPrice((p) => ({ ...p, [name]: value }));
  }
  if (!isAdmin()) return <Message negative content='需要管理员权限' />;
  return (
    <main className='platform-shell platform-admin-shell'>
      <div className='platform-heading'>
        <div>
          <div className='platform-eyebrow'>ATC / 管理控制台</div>
          <h1>平台运营</h1>
          <p>收支总览、钱包流水与模型运营管理。</p>
        </div>
        <Button
          disabled={busy}
          onClick={() => {
            load();
            setRefresh((value) => value + 1);
          }}
        >
          刷新
        </Button>
      </div>
      {error && <Message negative content={error} />}
      {notice && <Message positive content={notice} />}
      <div className='platform-admin-layout'>
        <nav className='platform-admin-nav' aria-label='平台运营二级菜单'>
          {sections.map(([id, name]) => (
            <button
              type='button'
              key={id}
              aria-current={tab === id ? 'page' : undefined}
              className={tab === id ? 'active' : ''}
              onClick={() => {
                setSearch({ view: id });
                setPage(0);
                setError('');
                setNotice('');
              }}
            >
              {name}
            </button>
          ))}
        </nav>
        <section className='platform-panel platform-admin-main'>
          {tab === 'overview' && <FinancialOverview refresh={refresh} />}
          {tab === 'entries' && <WalletLedger refresh={refresh} />}
          {tab === 'prices' && (
            <>
              <PricingOperations prices={prices} onRefresh={load} />
              <h2>新增模型 / 手动发布价格</h2>
              <p>
                金额单位：元 / 百万 Token。发布新版本不会修改历史账单。成本为 0
                表示尚未配置成本，不能据此判断实际利润。
              </p>
              <Form
                onSubmit={() =>
                  action(async () => {
                    if (!price.reason.trim()) throw new Error('请填写调价原因');
                    await request('post', '/api/platform/admin/prices', {
                      model: price.model,
                      reason: price.reason,
                      current_id: price.current_id,
                      strategy: 'fixed',
                      provider: price.provider,
                      kind: price.kind,
                      vision: price.vision,
                      input_rate: units(price.input),
                      cache_write_rate:
                        price.kind === 'responses'
                          ? units(price.cacheWrite)
                          : 0,
                      cache_read_rate:
                        price.kind === 'responses' ? units(price.cacheRead) : 0,
                      output_rate:
                        price.kind === 'embeddings' ? 0 : units(price.output),
                      cost_input_rate: units(price.costInput),
                      cost_cache_write_rate:
                        price.kind === 'responses'
                          ? units(price.costCacheWrite)
                          : 0,
                      cost_cache_read_rate:
                        price.kind === 'responses'
                          ? units(price.costCacheRead)
                          : 0,
                      cost_output_rate:
                        price.kind === 'embeddings'
                          ? 0
                          : units(price.costOutput),
                      max_input_tokens: Number(price.max_input_tokens),
                      default_output_tokens:
                        price.kind === 'chat'
                          ? Number(price.default_output_tokens)
                          : 0,
                      max_output_tokens:
                        price.kind === 'embeddings'
                          ? 0
                          : Number(price.max_output_tokens),
                      enabled: price.enabled,
                      test_price: price.test_price,
                    });
                    setPrice(blankPrice);
                  })
                }
              >
                <Form.Input
                  required
                  label='调价原因'
                  maxLength={512}
                  value={price.reason}
                  onChange={(_, v) => priceField('reason', v.value)}
                />
                <Form.Group widths='equal'>
                  <Form.Input
                    required
                    label='平台模型名称'
                    value={price.model}
                    onChange={(e) =>
                      setPrice({
                        ...price,
                        model: e.target.value,
                        current_id:
                          prices.find(
                            (p) => p.enabled && p.model === e.target.value
                          )?.id || 0,
                      })
                    }
                  />
                  <Form.Input
                    required
                    label='供应商'
                    value={price.provider}
                    onChange={(e) => priceField('provider', e.target.value)}
                  />
                  <Form.Select
                    label='接口类型'
                    options={[
                      { key: 'chat', text: '对话', value: 'chat' },
                      {
                        key: 'responses',
                        text: 'OpenAI Responses',
                        value: 'responses',
                      },
                      {
                        key: 'embeddings',
                        text: 'Embeddings',
                        value: 'embeddings',
                      },
                    ]}
                    value={price.kind}
                    onChange={(_, v) =>
                      setPrice((p) => ({ ...p, kind: v.value, vision: false }))
                    }
                  />
                </Form.Group>
                {price.kind === 'responses' && (
                  <Form.Group widths='equal'>
                    {[
                      ['cacheWrite', 'Cache Write 售价'],
                      ['cacheRead', 'Cache Read 售价'],
                      ['costCacheWrite', 'Cache Write 成本'],
                      ['costCacheRead', 'Cache Read 成本'],
                    ].map(([id, name]) => (
                      <Form.Input
                        key={id}
                        label={name}
                        value={price[id]}
                        onChange={(e) => priceField(id, e.target.value)}
                      />
                    ))}
                  </Form.Group>
                )}
                <Form.Group widths='equal'>
                  {[
                    ['input', '输入售价'],
                    ['output', '输出售价'],
                    ['costInput', '输入成本'],
                    ['costOutput', '输出成本'],
                  ].map(([id, name]) => (
                    <Form.Input
                      key={id}
                      label={name}
                      value={price[id]}
                      onChange={(e) => priceField(id, e.target.value)}
                      disabled={
                        price.kind === 'embeddings' &&
                        (id === 'output' || id === 'costOutput')
                      }
                    />
                  ))}
                </Form.Group>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='输入预占上限 Token'
                    type='number'
                    value={price.max_input_tokens}
                    onChange={(e) =>
                      priceField('max_input_tokens', e.target.value)
                    }
                  />
                  <Form.Input
                    label='默认输出 Token'
                    type='number'
                    value={price.default_output_tokens}
                    disabled={price.kind !== 'chat'}
                    onChange={(e) =>
                      priceField('default_output_tokens', e.target.value)
                    }
                  />
                  <Form.Input
                    label='最大输出 Token'
                    type='number'
                    value={price.max_output_tokens}
                    disabled={price.kind === 'embeddings'}
                    onChange={(e) =>
                      priceField('max_output_tokens', e.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Checkbox
                    label='支持图片理解'
                    checked={price.vision}
                    disabled={price.kind === 'embeddings'}
                    onChange={(_, v) => priceField('vision', v.checked)}
                  />
                  <Form.Checkbox
                    label='启用此版本'
                    checked={price.enabled}
                    onChange={(_, v) => priceField('enabled', v.checked)}
                  />
                  <Form.Checkbox
                    label='测试价格'
                    checked={price.test_price}
                    onChange={(_, v) => priceField('test_price', v.checked)}
                  />
                </Form.Group>
                <Button primary loading={busy} disabled={busy}>
                  发布版本
                </Button>
              </Form>
              <div className='platform-scroll'>
                <Table unstackable basic='very'>
                  <Table.Header>
                    <Table.Row>
                      {['版本', '模型', '输入 / 输出售价', '状态', '操作'].map(
                        (s) => (
                          <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
                        )
                      )}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {prices.map((p) => (
                      <Table.Row key={p.id}>
                        <Table.Cell>#{p.id}</Table.Cell>
                        <Table.Cell>
                          {p.model}{' '}
                          {p.test_price && <Label color='orange'>测试</Label>}
                        </Table.Cell>
                        <Table.Cell>
                          {yuan(p.input_rate)} / {yuan(p.output_rate)}
                          {p.kind === 'responses' && (
                            <>
                              <br />
                              Cache W/R {yuan(p.cache_write_rate)} /{' '}
                              {yuan(p.cache_read_rate)}
                            </>
                          )}
                        </Table.Cell>
                        <Table.Cell>{p.enabled ? '启用' : '停用'}</Table.Cell>
                        <Table.Cell>
                          <Button
                            size='mini'
                            disabled={busy || !p.enabled}
                            onClick={() =>
                              setPrice({
                                ...blankPrice,
                                model: p.model,
                                provider: p.provider,
                                kind: p.kind,
                                vision: p.vision,
                                input: String(p.input_rate / 1000000),
                                cacheWrite: String(
                                  p.cache_write_rate / 1000000
                                ),
                                cacheRead: String(p.cache_read_rate / 1000000),
                                output: String(p.output_rate / 1000000),
                                costInput: String(p.cost_input_rate / 1000000),
                                costCacheWrite: String(
                                  p.cost_cache_write_rate / 1000000
                                ),
                                costCacheRead: String(
                                  p.cost_cache_read_rate / 1000000
                                ),
                                costOutput: String(
                                  p.cost_output_rate / 1000000
                                ),
                                max_input_tokens: String(p.max_input_tokens),
                                default_output_tokens: String(
                                  p.default_output_tokens
                                ),
                                max_output_tokens: String(p.max_output_tokens),
                                test_price: p.test_price,
                                current_id: p.id,
                              })
                            }
                          >
                            复制到编辑表单
                          </Button>
                          <Button
                            size='mini'
                            disabled={!p.enabled || busy}
                            onClick={() =>
                              action(() =>
                                request(
                                  'delete',
                                  `/api/platform/admin/prices/${p.id}`
                                )
                              )
                            }
                          >
                            停用
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
          {tab === 'credit' && (
            <>
              <h2>人工充值</h2>
              <p>
                每笔充值记录操作人和原因。业务编号用于避免网络重试导致重复入账。
              </p>
              <Form
                onSubmit={() =>
                  action(async () => {
                    await request('post', '/api/platform/admin/credits', {
                      ...credit,
                      user_id: Number(credit.user_id),
                      amount: units(credit.amount),
                    });
                    setCredit({
                      user_id: '',
                      amount: '',
                      reason: '',
                      business_id: crypto.randomUUID(),
                    });
                  })
                }
              >
                {[
                  ['user_id', '用户 ID'],
                  ['amount', '人民币金额'],
                  ['reason', '充值原因'],
                  ['business_id', '业务编号'],
                ].map(([id, name]) => (
                  <Form.Input
                    key={id}
                    required
                    label={name}
                    value={credit[id]}
                    onChange={(e) =>
                      setCredit((c) => ({ ...c, [id]: e.target.value }))
                    }
                  />
                ))}
                <Button primary loading={busy} disabled={busy}>
                  确认入账
                </Button>
              </Form>
            </>
          )}
          {tab === 'codes' && (
            <>
              <h2>生成兑换码</h2>
              <Form
                onSubmit={() =>
                  action(async () => {
                    const data = await request('post', '/api/redemption/', {
                      name: gift.name,
                      quota: units(gift.amount),
                      count: Number(gift.count),
                      expires_at: gift.expires_at
                        ? Math.floor(new Date(gift.expires_at).getTime() / 1000)
                        : 0,
                    });
                    setCodes(data);
                  })
                }
              >
                {[
                  ['name', '批次名称'],
                  ['amount', '人民币面额'],
                  ['count', '数量（1–100）'],
                ].map(([id, name]) => (
                  <Form.Input
                    key={id}
                    required
                    label={name}
                    value={gift[id]}
                    onChange={(e) =>
                      setGift((g) => ({ ...g, [id]: e.target.value }))
                    }
                  />
                ))}
                <Form.Input
                  type='datetime-local'
                  label='到期时间（留空表示不过期）'
                  value={gift.expires_at}
                  onChange={(e) =>
                    setGift((g) => ({ ...g, expires_at: e.target.value }))
                  }
                />
                <Button primary loading={busy} disabled={busy}>
                  生成
                </Button>
                {codes.length > 0 && (
                  <Form.TextArea
                    label='兑换码（请妥善保存）'
                    readOnly
                    value={codes.join('\n')}
                  />
                )}
              </Form>
            </>
          )}
          {tab === 'calls' && (
            <>
              <h2>调用核算</h2>
              <Form.Select
                label='记录范围'
                options={[
                  { key: 'pending', value: 'pending', text: '待核算及处理中' },
                  { key: 'all', value: 'all', text: '全部调用' },
                ]}
                value={callState}
                onChange={(_, v) => {
                  setCallState(v.value);
                  setPage(0);
                }}
              />
              <p>
                待核算记录保留冻结金额，需结合供应商账单确认费用。核算需要供应商账单或明确的未计费证据，并记录操作人及原因。
              </p>
              <div className='platform-scroll'>
                <Table unstackable basic='very'>
                  <Table.Header>
                    <Table.Row>
                      {[
                        '用户',
                        '模型',
                        '状态',
                        '预占',
                        '售价 / 成本',
                        '请求 ID',
                        '备注',
                        '操作',
                      ].map((s) => (
                        <Table.HeaderCell key={s}>{s}</Table.HeaderCell>
                      ))}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {calls.map((c) => (
                      <Table.Row key={c.id}>
                        <Table.Cell>{c.user_id}</Table.Cell>
                        <Table.Cell>{c.model}</Table.Cell>
                        <Table.Cell>{states[c.state]}</Table.Cell>
                        <Table.Cell>{yuan(c.reserved)}</Table.Cell>
                        <Table.Cell>
                          {yuan(c.charged)} / {yuan(c.cost)}
                          <br />
                          售价 #{c.price_id} · 成本{' '}
                          {c.cost_version_id
                            ? `#${c.cost_version_id}`
                            : '模型基准'}
                          <br />
                          {c.cost_basis === 'allocated'
                            ? '套餐分摊估算'
                            : '按量 / 基准'}
                        </Table.Cell>
                        <Table.Cell>
                          <code>{c.request_id}</code>
                        </Table.Cell>
                        <Table.Cell>{c.note}</Table.Cell>
                        <Table.Cell>
                          <Button
                            size='mini'
                            disabled={c.state !== 'pending' || busy}
                            onClick={() =>
                              setResolve({
                                request: c.request_id,
                                release: false,
                                input: '0',
                                cacheWrite: '0',
                                cacheRead: '0',
                                output: '0',
                                reasoning: '0',
                                reason: '',
                              })
                            }
                          >
                            核算
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              {resolve.request && (
                <Form
                  onSubmit={() =>
                    action(async () => {
                      await request(
                        'post',
                        `/api/platform/admin/requests/${encodeURIComponent(
                          resolve.request
                        )}/resolve`,
                        {
                          release: resolve.release,
                          prompt_tokens: Number(resolve.input),
                          cache_write_tokens: Number(resolve.cacheWrite),
                          cache_read_tokens: Number(resolve.cacheRead),
                          completion_tokens: Number(resolve.output),
                          reasoning_tokens: Number(resolve.reasoning),
                          reason: resolve.reason,
                        }
                      );
                      setResolve((r) => ({ ...r, request: '' }));
                    })
                  }
                >
                  <h3>核算请求 {resolve.request}</h3>
                  <Form.Checkbox
                    label='供应商确认未计费，释放全部预占'
                    checked={resolve.release}
                    onChange={(_, v) =>
                      setResolve((r) => ({ ...r, release: v.checked }))
                    }
                  />
                  <Form.Group widths='equal'>
                    {[
                      ['input', '实际输入 Token'],
                      ['cacheWrite', '其中 Cache Write Token'],
                      ['cacheRead', '其中 Cache Read Token'],
                      ['output', '实际输出 Token（含思考）'],
                      ['reasoning', '其中思考 Token'],
                    ].map(([id, name]) => (
                      <Form.Input
                        key={id}
                        type='number'
                        min='0'
                        label={name}
                        disabled={resolve.release}
                        value={resolve[id]}
                        onChange={(e) =>
                          setResolve((r) => ({ ...r, [id]: e.target.value }))
                        }
                      />
                    ))}
                  </Form.Group>
                  <Form.Input
                    required
                    label='核算原因 / 证据说明'
                    value={resolve.reason}
                    onChange={(e) =>
                      setResolve((r) => ({ ...r, reason: e.target.value }))
                    }
                  />
                  <Button primary loading={busy} disabled={busy}>
                    确认核算
                  </Button>
                  <Button
                    type='button'
                    onClick={() => setResolve((r) => ({ ...r, request: '' }))}
                  >
                    取消
                  </Button>
                </Form>
              )}
            </>
          )}
          {tab === 'calls' && (
            <div>
              <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
                上一页
              </Button>
              <span>第 {page + 1} 页</span>
              <Button
                disabled={calls.length < 50}
                onClick={() => setPage(page + 1)}
              >
                下一页
              </Button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
